import { TypedDocumentNode, gql } from '@apollo/client';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled, { CSSProperties } from 'styled-components';

import {
  CardQuality,
  DivisionChange,
  InGameCurrency,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { groupBy, sortBy } from '@sorare/core/src/lib/arrays';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { isType, isTypeFn } from '@sorare/core/src/lib/gql';
import { qualityNames } from '@sorare/core/src/lib/players';
import { isEnergyCurrency, isXPCurrency } from '@sorare/core/src/lib/rewards';
import { RANKED_SCARCITY } from '@sorare/core/src/lib/scarcity';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CompactContender } from 'components/contender/CompactContender';
import { RewardBoostApplied } from 'components/fixture/FixtureTeamsOverview/RewardBoostApplied';
import { AnyRewardConfig } from 'components/rewards/AnyRewardConfig';
import { ClickableAnyRewardConfig } from 'components/rewards/ClickableAnyRewardConfig';
import { NeonRewards } from 'components/rewards/NeonRewards';
import { Variant } from 'components/rewards/RewardOverview/types';

import { FixtureOverviewTitle } from './FixtureOverviewTitle';
import type { FixtureOverview_anyRewardConfigInterface } from './__generated__/index.graphql';
import {
  FixtureOverview_so5Fixture,
  FixtureOverview_so5RewardOrConfig,
} from './__generated__/index.graphql';

const Root = styled(Vertical)`
  @media ${laptopAndAbove} {
    min-width: 420px;
  }
`;
const RigidSquareLabelL = styled(LabelL)`
  font-family: var(--brand-font);
`;

const StyledIconButton = styled(IconButton)`
  margin-left: auto;
`;
const Block = styled.div`
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  background: var(--c-nd-50);
`;
const WrapHorizontal = styled(Horizontal)`
  flex-wrap: wrap;
  max-height: 135px;
  overflow-y: auto;
`;
const DivisionChanges = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--quadruple-unit);
`;
const Progress = styled.div`
  width: 100%;
  height: var(--unit);
  border-radius: var(--unit);
  background: var(--bg-color);
  position: relative;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: calc(100% - var(--progress));
    background: var(--progress-color);
    border-radius: var(--double-unit);
  }
`;

type Props = {
  so5Fixture?: FixtureOverview_so5Fixture;
  so5Rewards?: FixtureOverview_so5RewardOrConfig[];
  onClose?: () => void;
  scrollToContender?: (contender: string) => void;
  onlyRewards?: boolean;
};
export const FixtureOverview = ({
  so5Fixture,
  so5Rewards,
  onClose,
  scrollToContender,
  onlyRewards,
}: Props) => {
  const isLaptopAndAbove = useIsLaptop();

  const { myEligibleOrSo5Rewards, mySo5Lineups, first10So5Rankings } =
    so5Fixture || {};
  const so5RewardsOrConfigs = so5Rewards || myEligibleOrSo5Rewards || [];
  const rewardConfigs = so5RewardsOrConfigs.flatMap(so5RewardOrConfig =>
    isType(so5RewardOrConfig, 'So5RewardConfig')
      ? so5RewardOrConfig.rewardConfigs
      : so5RewardOrConfig.rewards.map(reward => reward.config)
  );
  const hasRewardBoostApplied = mySo5Lineups?.some(
    so5Lineup => (so5Lineup.rewardMultiplier || 1) > 1
  );

  const cashRewardConfigs = rewardConfigs.filter(
    isTypeFn('MonetaryRewardConfig')
  );

  const boxRewardConfigs = rewardConfigs.filter(
    isTypeFn('ProbabilisticBundleRewardConfig')
  );
  const sortedBoxRewardConfigs = boxRewardConfigs.sort((a, b) => {
    const configA = a.probabilisticBundleConfig;
    const configB = b.probabilisticBundleConfig;

    const scarcityCompare =
      RANKED_SCARCITY[configB.rarity] - RANKED_SCARCITY[configA.rarity];
    const starCompare = (configB.stars || 0) - (configA.stars || 0);

    return scarcityCompare || starCompare;
  });

  const cardRewardConfigs = rewardConfigs.filter(isTypeFn('CardRewardConfig'));
  const cardCountsPerRarityPerQuality = cardRewardConfigs.reduce(
    (acc, rewardConfig) => {
      const { rarity, quality } = rewardConfig;
      if (!rarity || !quality) {
        return acc;
      }

      return {
        ...acc,
        [rarity]: {
          ...acc[rarity],
          count: (acc[rarity]?.count || 0) + 1,
          [quality]: (acc[rarity]?.[quality] || 0) + 1,
        },
      };
    },
    {} as Record<Rarity, { count: number } & Record<CardQuality, number>>
  );

  const shardRewardConfigs = rewardConfigs.filter(
    isTypeFn('CardShardRewardConfig')
  );
  const shardCountsPerRarity = shardRewardConfigs.reduce(
    (acc, rewardConfig) => {
      const { rarity, quantity } = rewardConfig;

      return {
        ...acc,
        [rarity]: (acc[rarity] || 0) + quantity,
      };
    },
    {} as Record<Rarity, number>
  );

  const xpRewardConfigs = rewardConfigs
    .filter(isTypeFn('InGameCurrencyRewardConfig'))
    .filter(config => isXPCurrency(config.currency));

  const xpCountsPerCurrency = xpRewardConfigs.reduce(
    (acc, rewardConfig) => {
      const { amount, currency } = rewardConfig;

      return {
        ...acc,
        [currency]: (acc[currency] || 0) + amount,
      };
    },
    {} as Record<InGameCurrency, number>
  );
  const energyRewardConfigs = rewardConfigs
    .filter(isTypeFn('InGameCurrencyRewardConfig'))
    .filter(config => isEnergyCurrency(config.currency));

  const energyCountsPerCurrency = energyRewardConfigs.reduce(
    (acc, rewardConfig) => {
      const { amount, currency } = rewardConfig;

      return {
        ...acc,
        [currency]: (acc[currency] || 0) + amount,
      };
    },
    {} as Record<InGameCurrency, number>
  );

  const otherRewardConfigs = rewardConfigs.filter(
    rewardConfig =>
      !isType(rewardConfig, 'CardRewardConfig') &&
      !isType(rewardConfig, 'ProbabilisticBundleRewardConfig') &&
      !isType(rewardConfig, 'MonetaryRewardConfig') &&
      !isType(rewardConfig, 'InGameCurrencyRewardConfig') &&
      !isType(rewardConfig, 'CardShardRewardConfig')
  );

  const eligibleDivisionChanges = (mySo5Lineups || [])
    .flatMap(so5Lineup => so5Lineup.so5Rankings.at(0)?.eligibleDivisionChange)
    .filter(Boolean);

  const divisionChangesPerChange = groupBy(
    eligibleDivisionChanges,
    change => change
  );

  const topContenders = (first10So5Rankings || []).map(
    ({ so5Lineup }) => so5Lineup.so5LeaderboardContender
  );

  const mainRewardsPresent =
    shardRewardConfigs.length > 0 ||
    cardRewardConfigs.length > 0 ||
    cashRewardConfigs.length > 0 ||
    boxRewardConfigs.length > 0;

  const displayRecapBoxes =
    shardRewardConfigs.length +
      cardRewardConfigs.length +
      cashRewardConfigs.length <
      3 && boxRewardConfigs.length > 0;

  const fancyRewardsPresent = otherRewardConfigs.length > 0;

  return (
    <Root gap={6}>
      {!isLaptopAndAbove && onClose && (
        <StyledIconButton
          icon={faTimes}
          color="transparent"
          smaller
          onClick={onClose}
        />
      )}
      {!onlyRewards && so5Fixture && (
        <Vertical center gap={2.5}>
          <StarBall width={40} height={40} />
          <RigidSquareLabelL bold uppercase color="var(--c-nd-600)">
            <FixtureOverviewTitle so5Fixture={so5Fixture} />
          </RigidSquareLabelL>
        </Vertical>
      )}
      {mainRewardsPresent && (
        <NeonRewards
          displayCommonEssence={false}
          displayRecapBoxes={displayRecapBoxes}
          so5RewardsOrConfigs={so5RewardsOrConfigs}
        />
      )}
      {hasRewardBoostApplied && (
        <Vertical center>
          <RewardBoostApplied />
        </Vertical>
      )}
      <Vertical gap={3}>
        {boxRewardConfigs.length > 0 && (
          <Vertical gap={1.5}>
            <LabelM bold color="var(--c-nd-600)">
              <FormattedMessage {...fantasy.boxes} />
            </LabelM>
            <Block>
              <WrapHorizontal>
                {sortedBoxRewardConfigs.map((rewardConfig, i) => (
                  <ClickableAnyRewardConfig
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    anyRewardConfig={rewardConfig}
                    variant={Variant.CLASSIC}
                    size={5}
                  />
                ))}
              </WrapHorizontal>
            </Block>
          </Vertical>
        )}
        {cardRewardConfigs.length > 0 && (
          <Vertical gap={1.5}>
            <LabelM bold color="var(--c-nd-600)">
              <FormattedMessage {...glossary.cards} />
            </LabelM>
            <Block>
              <Vertical gap={2}>
                {Object.entries(cardCountsPerRarityPerQuality).map(
                  ([rarity, { count, ...qualities }]) => (
                    <WrapHorizontal key={rarity}>
                      <AnyRewardConfig
                        anyRewardConfig={{
                          __typename: 'CardRewardConfig',
                          id: '',
                          rarity: rarity as Rarity,
                          quality: null,
                        }}
                        variant={Variant.INLINE}
                        count={count}
                      />
                      {sortBy(entry => entry[0], Object.entries(qualities)).map(
                        ([quality, qualityCount]) => (
                          <Chip key={quality}>
                            <Horizontal gap={0.5}>
                              <LabelM bold color="var(--c-white)">
                                <FormattedNumber value={qualityCount} />
                              </LabelM>
                              <LabelM color="var(--c-nd-600)">
                                {qualityNames[quality as CardQuality]}
                              </LabelM>
                            </Horizontal>
                          </Chip>
                        )
                      )}
                    </WrapHorizontal>
                  )
                )}
              </Vertical>
            </Block>
          </Vertical>
        )}
        {shardRewardConfigs.length > 0 && (
          <Vertical gap={1.5}>
            <LabelM bold color="var(--c-nd-600)">
              <FormattedMessage {...glossary.essence} />
            </LabelM>
            <Block>
              <WrapHorizontal>
                {Object.entries(shardCountsPerRarity).map(
                  ([rarity, count], i) => (
                    <ClickableAnyRewardConfig
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      anyRewardConfig={{
                        __typename: 'CardShardRewardConfig',
                        id: '',
                        rarity: rarity as Rarity,
                        quantity: count,
                      }}
                      variant={Variant.INLINE}
                      size={5}
                    />
                  )
                )}
              </WrapHorizontal>
            </Block>
          </Vertical>
        )}

        {energyRewardConfigs.length > 0 && (
          <Vertical gap={1.5}>
            <LabelM bold color="var(--c-nd-600)">
              <FormattedMessage
                id="FixtureOverview.energy"
                defaultMessage="Energy"
              />
            </LabelM>
            <Block>
              <WrapHorizontal>
                {Object.entries(energyCountsPerCurrency).map(
                  ([currency, count], i) => (
                    <ClickableAnyRewardConfig
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      anyRewardConfig={{
                        __typename: 'InGameCurrencyRewardConfig',
                        id: '',
                        currency: currency as InGameCurrency,
                        amount: count,
                      }}
                      variant={Variant.INLINE}
                      size={5}
                    />
                  )
                )}
              </WrapHorizontal>
            </Block>
          </Vertical>
        )}
        {xpRewardConfigs.length > 0 && (
          <Vertical gap={1.5}>
            <LabelM bold color="var(--c-nd-600)">
              <FormattedMessage id="FixtureOverview.xp" defaultMessage="XP" />
            </LabelM>
            <Block>
              <WrapHorizontal>
                {Object.entries(xpCountsPerCurrency).map(
                  ([currency, count], i) => (
                    <ClickableAnyRewardConfig
                      // eslint-disable-next-line react/no-array-index-key
                      key={i}
                      anyRewardConfig={{
                        __typename: 'InGameCurrencyRewardConfig',
                        id: '',
                        currency: currency as InGameCurrency,
                        amount: count,
                      }}
                      variant={Variant.INLINE}
                      size={5}
                    />
                  )
                )}
              </WrapHorizontal>
            </Block>
          </Vertical>
        )}
        {onlyRewards ? (
          <>
            {fancyRewardsPresent && (
              <Vertical gap={1.5}>
                <LabelM bold color="var(--c-nd-600)">
                  <FormattedMessage
                    id="FixtureOverview.specials"
                    defaultMessage="Specials"
                  />
                </LabelM>
                <Block>
                  <WrapHorizontal>
                    {otherRewardConfigs.map((rewardConfig, i) => (
                      <ClickableAnyRewardConfig
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        anyRewardConfig={rewardConfig}
                        variant={Variant.CLASSIC}
                        size={5}
                      />
                    ))}
                  </WrapHorizontal>
                </Block>
              </Vertical>
            )}
          </>
        ) : (
          <>
            {eligibleDivisionChanges.length > 0 && (
              <Vertical gap={1.5}>
                <LabelM bold color="var(--c-nd-600)">
                  <FormattedMessage
                    id="FixtureOverview.overallPerformance"
                    defaultMessage="Overall Performance"
                  />
                </LabelM>
                <Block>
                  <DivisionChanges>
                    <Vertical center>
                      <LabelS>
                        <FormattedMessage {...fantasy.promotion} />
                      </LabelS>
                      <Progress
                        style={
                          {
                            '--bg-color': 'rgba(var(--c-rgb-rivals-win), .2)',
                            '--progress-color': 'var(--c-rivals-win)',
                            '--progress': `${
                              ((divisionChangesPerChange[
                                DivisionChange.PROMOTED
                              ]?.length || 0) /
                                eligibleDivisionChanges.length) *
                              100
                            }%`,
                          } as CSSProperties
                        }
                      />
                      <LabelS color="var(--c-rivals-win)">
                        <FormattedNumber
                          value={
                            divisionChangesPerChange[DivisionChange.PROMOTED]
                              ?.length || 0
                          }
                        />
                      </LabelS>
                    </Vertical>
                    <Vertical center>
                      <LabelS>
                        <FormattedMessage
                          id="FixtureOverview.safety"
                          defaultMessage="Safety"
                        />
                      </LabelS>
                      <Progress
                        style={
                          {
                            '--bg-color': 'rgba(var(--c-rgb-white), .1)',
                            '--progress-color': 'var(--c-white)',
                            '--progress': `${
                              ((divisionChangesPerChange[DivisionChange.STABLE]
                                ?.length || 0) /
                                eligibleDivisionChanges.length) *
                              100
                            }%`,
                          } as CSSProperties
                        }
                      />
                      <LabelS>
                        <FormattedNumber
                          value={
                            divisionChangesPerChange[DivisionChange.STABLE]
                              ?.length || 0
                          }
                        />
                      </LabelS>
                    </Vertical>
                    <Vertical center>
                      <LabelS>
                        <FormattedMessage {...fantasy.relegation} />
                      </LabelS>
                      <Progress
                        style={
                          {
                            '--bg-color': 'rgba(var(--c-rgb-rivals-loss), .2)',
                            '--progress-color': 'var(--c-rivals-loss)',
                            '--progress': `${
                              ((divisionChangesPerChange[
                                DivisionChange.RELEGATED
                              ]?.length || 0) /
                                eligibleDivisionChanges.length) *
                              100
                            }%`,
                          } as CSSProperties
                        }
                      />
                      <LabelS color="var(--c-rivals-loss)">
                        <FormattedNumber
                          value={
                            divisionChangesPerChange[DivisionChange.RELEGATED]
                              ?.length || 0
                          }
                        />
                      </LabelS>
                    </Vertical>
                  </DivisionChanges>
                </Block>
              </Vertical>
            )}
            {topContenders.length > 0 && (
              <Vertical gap={1.5}>
                <LabelM bold color="var(--c-nd-600)">
                  <FormattedMessage
                    id="FixtureOverview.topRankingTeams"
                    defaultMessage="Top Ranking Teams"
                  />
                </LabelM>
                <Vertical gap={2}>
                  {topContenders.map(contender => (
                    <CompactContender
                      key={contender.slug}
                      so5LeaderboardContender={contender}
                      onClick={() => {
                        scrollToContender?.(contender.slug);
                        onClose?.();
                      }}
                    />
                  ))}
                </Vertical>
              </Vertical>
            )}
          </>
        )}
      </Vertical>
    </Root>
  );
};

const anyRewardConfigInterfaceFragment = gql`
  fragment FixtureOverview_anyRewardConfigInterface on AnyRewardConfigInterface {
    id
    ... on MonetaryRewardConfig {
      id
      monetaryAmount: amount {
        referenceCurrency
        eur
        gbp
        usd
        wei
      }
    }
    ... on ProbabilisticBundleRewardConfig {
      id
      probabilisticBundleConfig {
        id
        rarity
        stars
      }
    }
    ... on CardShardRewardConfig {
      id
      quantity
      rarity
    }
    ... on InGameCurrencyRewardConfig {
      id
      amount
      currency
    }
    ...ClickableAnyRewardConfig_anyRewardConfigInterface
  }
  ${ClickableAnyRewardConfig.fragments.anyRewardConfigInterface}
` as TypedDocumentNode<FixtureOverview_anyRewardConfigInterface>;

const so5RewardOrConfigFragment = gql`
  fragment FixtureOverview_so5RewardOrConfig on So5RewardOrConfig {
    ... on So5RewardConfig {
      score
      ranks
      rankPct
      rewardConfigs {
        id
        ...FixtureOverview_anyRewardConfigInterface
      }
    }
    ... on So5Reward {
      slug
      rewards {
        id
        config {
          id
          ...FixtureOverview_anyRewardConfigInterface
        }
      }
    }
    ...NeonRewards_so5RewardOrConfig
  }
  ${anyRewardConfigInterfaceFragment}
  ${NeonRewards.fragments.so5RewardOrConfig}
` as TypedDocumentNode<FixtureOverview_so5RewardOrConfig>;

FixtureOverview.fragments = {
  so5RewardOrConfig: so5RewardOrConfigFragment,
  so5Fixture: gql`
    fragment FixtureOverview_so5Fixture on So5Fixture {
      slug
      first10So5Rankings: mySo5Rankings(first: 10, withTraining: false) {
        id
        so5Lineup {
          id
          so5LeaderboardContender {
            slug
            ...CompactContender_so5LeaderboardContender
          }
        }
      }
      myEligibleOrSo5Rewards {
        ...FixtureOverview_so5RewardOrConfig
      }
      mySo5Lineups {
        id
        rewardMultiplier
        so5Leaderboard {
          slug
          mainRarityType
        }
        so5Rankings {
          id
          eligibleDivisionChange
        }
      }
      ...FixtureOverviewTitle_so5Fixture
    }
    ${so5RewardOrConfigFragment}
    ${CompactContender.fragments.so5LeaderboardContender}
    ${FixtureOverviewTitle.fragments.so5Fixture}
  ` as TypedDocumentNode<FixtureOverview_so5Fixture>,
};
